import { measureBandwidth } from '../useSharedVideoPlayer/bandwidthDetector';
import { cacheResponse, getCachedResponse } from './videoSegmentCache';

interface ShakaNetworkResponse {
	uri: string;
	originalUri: string;
	data: ArrayBuffer;
	status: number;
	headers: Record<string, string>;
	fromCache?: boolean;
}

interface ShakaNetworkRequest {
	method?: string;
	headers?: HeadersInit;
	body?: BodyInit | null;
}

interface ShakaModule {
	polyfill: { installAll(): void };
	Player: {
		isBrowserSupported(): boolean;
		new (el?: HTMLMediaElement): unknown;
	};
	net: {
		NetworkingEngine: {
			registerScheme(
				scheme: string,
				handler: (uri: string, request: ShakaNetworkRequest) => unknown,
				priority: number
			): void;
			PluginPriority: { APPLICATION: number };
		};
	};
	util: {
		Error: {
			new (
				severity: number,
				category: number,
				code: number,
				...args: unknown[]
			): Error;
			Severity: { RECOVERABLE: number };
			Category: { NETWORK: number };
			Code: { BAD_HTTP_STATUS: number };
		};
		AbortableOperation: {
			new (
				promise: Promise<ShakaNetworkResponse>,
				abort: () => Promise<void>
			): unknown;
		};
	};
}

let shakaInstance: ShakaModule | null = null;
let cacheInitialized = false;

// Version hash from manifest URL, propagated to segment requests (keyed by URL prefix to avoid race conditions)
const VERSION_MAP_MAX_SIZE = 50;
const versionHashMap = new Map<string, string>();

function getVersionedUrl(url: string): string {
	const urlPrefix = url.substring(0, url.lastIndexOf('/'));
	const isManifest = url.includes('.m3u8');

	if (isManifest) {
		const match = url.match(/[?&]v=([^&]+)/);
		if (match?.[1]) {
			if (versionHashMap.size >= VERSION_MAP_MAX_SIZE) {
				const firstKey = versionHashMap.keys().next().value;
				if (firstKey) versionHashMap.delete(firstKey);
			}
			versionHashMap.set(urlPrefix, match[1]);
		}
	}

	let versionHash = versionHashMap.get(urlPrefix);

	if (!versionHash) {
		let parentPrefix = urlPrefix;
		while (
			!versionHash &&
			parentPrefix.includes('/', parentPrefix.indexOf('//') + 2)
		) {
			parentPrefix = parentPrefix.substring(0, parentPrefix.lastIndexOf('/'));
			versionHash = versionHashMap.get(parentPrefix);
		}
		if (versionHash) {
			versionHashMap.set(urlPrefix, versionHash);
		}
	}

	if (versionHash && !url.includes('v=')) {
		const isHlsFile = url.includes('.m3u8') || url.includes('.ts');
		if (isHlsFile) {
			return `${url}${url.includes('?') ? '&' : '?'}v=${versionHash}`;
		}
	}

	return url;
}

function initSegmentCache(shaka: ShakaModule): void {
	if (cacheInitialized) return;

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

	shaka.net.NetworkingEngine.registerScheme(
		'https',
		(uri: string, request: ShakaNetworkRequest) => {
			const abortController = new AbortController();

			const promise = (async () => {
				const finalUri = getVersionedUrl(uri);

				try {
					const cached = await getCachedResponse(finalUri);
					if (cached) {
						return {
							uri: finalUri,
							originalUri: uri,
							data: await cached.arrayBuffer(),
							status: 200,
							headers: {},
							fromCache: true
						};
					}
				} catch (cacheErr) {
					// Cache lookup failed, proceed with fetch
				}

				const fetchStart = performance.now();
				const response = await fetch(finalUri, {
					method: request.method || 'GET',
					headers: request.headers,
					body: request.body,
					signal: abortController.signal
				});
				const fetchDuration = performance.now() - fetchStart;

				if (!response.ok) {
					throw new shaka.util.Error(
						shaka.util.Error.Severity.RECOVERABLE,
						shaka.util.Error.Category.NETWORK,
						shaka.util.Error.Code.BAD_HTTP_STATUS,
						finalUri,
						response.status
					);
				}

				const data = await response.arrayBuffer();
				const totalDuration = performance.now() - fetchStart;

				// Measure real bandwidth from this download
				const measuredMbps = measureBandwidth(data.byteLength, totalDuration);

				// Cache HLS resources (manifests + segments) so prefetch and
				// Shaka share the same CacheStorage and avoid re-downloads.
				const isHlsResource =
					finalUri.includes('.m3u8') || finalUri.includes('.ts');
				if (isHlsResource) {
					try {
						await cacheResponse(
							finalUri,
							new Response(data, {
								status: response.status,
								headers: response.headers
							})
						);
					} catch (cacheErr) {
						// Cache store failed, but we still return the data
					}
				}

				return {
					uri: finalUri,
					originalUri: uri,
					data,
					status: response.status,
					headers: {}
				};
			})().catch(err => {
				throw err;
			});

			return new shaka.util.AbortableOperation(promise, () => {
				abortController.abort();
				return Promise.resolve();
			});
		},
		shaka.net.NetworkingEngine.PluginPriority.APPLICATION
	);

	cacheInitialized = true;
}

export async function loadShaka(): Promise<ShakaModule> {
	if (!import.meta.client) {
		throw new Error('Shaka Player can only be loaded on the client');
	}

	if (shakaInstance) {
		return shakaInstance;
	}

	try {
		const shakaModule = await import('shaka-player');
		shakaInstance = (shakaModule.default || shakaModule) as ShakaModule;

		shakaInstance.polyfill.installAll();

		// Initialize custom segment cache handler
		initSegmentCache(shakaInstance);

		return shakaInstance;
	} catch (err) {
		throw err;
	}
}

export function isBrowserSupported(): boolean {
	return shakaInstance?.Player.isBrowserSupported() ?? false;
}

export { clearVideoCache } from './videoSegmentCache';
